// src/pages/SignupPage.jsx
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  EyeIcon,
  EyeSlashIcon,
  UserIcon,
  AtSymbolIcon,
  PhoneIcon,
  LockClosedIcon,
} from "@heroicons/react/24/solid";
import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";
import { signupSchema } from "../../validation/auth.schema";
import carImage from "../../assets/Car_hero1.avif"; // ← put your image here
import logo from "../../assets/Full logo colour, white bg.jpg";
import logo2 from "../../assets/Full logo colour.jpg";

export default function SignupPage() {
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [showPwd, setShowPwd] = useState(false);
  const [serverError, setServerError] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(signupSchema),
    defaultValues: { fullName: "", email: "", phone: "", password: "" },
  });

  const onSubmit = async (data) => {
    setServerError("");
    try {
      const { data: res } = await api.post("/auth/register", data);
      localStorage.setItem("accessToken", res.accessToken);
      setAuth({ user: res.user, token: res.accessToken });

      if (res.user.role === "super-admin") navigate("/admin/vehicles");
      else if (["car-admin", "image-admin"].includes(res.user.role))
        navigate("/admin");
      else navigate("/used-cars");
    } catch (err) {
      setServerError(err.response?.data?.message || "Registration failed");
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 min-h-screen font-sans">
      {/* ─── Left: Branding ─── */}
      <div className="hidden md:flex items-center justify-center bg-white from-primary to-secondary pt-14">
        <div
          onClick={() => navigate("/")}
          className="text-center text-darksec cursor-pointer"
        >
          {/* logo */}
          <Link to="/" className="flex items-center justify-center">
            <img
              src={logo}
              alt="ZORQK Logo"
              className="h-16 w-auto object-contain"
            />
          </Link>
          <p className="text-primary text-lg opacity-75">
            Join ZORQK and accelerate your car-trading journey.
          </p>
          <img
            src={carImage}
            alt="Sleek car"
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* ─── Right: Form ─── */}
      <div className="flex items-center justify-center p-6 bg-bg">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full max-w-md px-2 py-6 space-y-6"
        >
          <div
            onClick={() => navigate("/")}
            className="md:hidden text-center text-darksec cursor-pointer"
          >
            {/* logo */}
            <Link to="/" className="flex items-center justify-center">
              <img
                src={logo2}
                alt="ZORQK Logo"
                className="h-10 w-auto object-contain"
              />
            </Link>
          </div>
          <h2 className="text-2xl font-semibold text-foreground dark:text-foreground text-center">
            Create your account
          </h2>

          {serverError && (
            <div className="text-sm text-danger bg-danger/10 px-3 py-2 rounded">
              {serverError}
            </div>
          )}

          {/* Full Name */}
          <div className="space-y-1">
            <label
              htmlFor="fullName"
              className="block text-sm font-medium text-foreground dark:text-foreground"
            >
              Name
            </label>
            <div className="relative">
              <UserIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 text-secondary dark:text-secondary-accent -translate-y-1/2" />
              <input
                id="fullName"
                {...register("fullName")}
                placeholder="Full Name"
                className="w-full pl-10 pr-4 py-2 border border-secondary-accent dark:border-secondary rounded-lg
                            placeholder-secondary-accent dark:placeholder-secondary
                           focus:outline-none focus:ring-2 focus:ring-primary transition"
              />
            </div>
            {errors.fullName && (
              <p className="text-sm text-danger">{errors.fullName.message}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-1">
            <label
              htmlFor="email"
              className="block text-sm font-medium text-foreground dark:text-foreground"
            >
              Email
            </label>
            <div className="relative">
              <AtSymbolIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 text-secondary dark:text-secondary-accent -translate-y-1/2" />
              <input
                id="email"
                type="email"
                {...register("email")}
                placeholder="you@example.com"
                className="w-full pl-10 pr-4 py-2 border border-secondary-accent dark:border-secondary rounded-lg
                            placeholder-secondary-accent dark:placeholder-secondary
                           focus:outline-none focus:ring-2 focus:ring-primary transition"
              />
            </div>
            {errors.email && (
              <p className="text-sm text-danger">{errors.email.message}</p>
            )}
          </div>

          {/* Phone */}
          <div className="space-y-1">
            <label
              htmlFor="phone"
              className="block text-sm font-medium text-foreground dark:text-foreground"
            >
              Phone Number
            </label>
            <div className="relative">
              <PhoneIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 text-secondary dark:text-secondary-accent -translate-y-1/2" />
              <input
                id="phone"
                type="tel"
                {...register("phone")}
                placeholder="+1234567890"
                className="w-full pl-10 pr-4 py-2 border border-secondary-accent dark:border-secondary rounded-lg
                            placeholder-secondary-accent dark:placeholder-secondary
                           focus:outline-none focus:ring-2 focus:ring-primary transition"
              />
            </div>
            {errors.phone && (
              <p className="text-sm text-danger">{errors.phone.message}</p>
            )}
          </div>

          {/* Password */}
          <div className="space-y-1">
            <label
              htmlFor="password"
              className="block text-sm font-medium text-foreground dark:text-foreground"
            >
              Password
            </label>
            <div className="relative">
              <LockClosedIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 text-secondary dark:text-secondary-accent -translate-y-1/2" />
              <input
                id="password"
                type={showPwd ? "text" : "password"}
                {...register("password")}
                placeholder="••••••••"
                className="w-full pl-10 pr-10 py-2 border border-secondary-accent dark:border-secondary rounded-lg
                           placeholder-secondary-accent dark:placeholder-secondary
                           focus:outline-none focus:ring-2 focus:ring-primary transition"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-secondary dark:text-secondary-accent"
                aria-label={showPwd ? "Hide password" : "Show password"}
              >
                {showPwd ? <EyeSlashIcon /> : <EyeIcon />}
              </button>
            </div>
            {errors.password && (
              <p className="text-sm text-danger">{errors.password.message}</p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 font-semibold rounded-lg
              bg-accent text-darksec shadow-md
              hover:bg-primary/90 transition
              ${isSubmitting ? "opacity-70 cursor-not-allowed" : ""}`}
          >
            {isSubmitting ? "Creating…" : "Create My Account"}
          </button>

          {/* Already have an account */}
          <p className="text-center text-sm text-secondary dark:text-secondary-accent">
            Already have an account?{" "}
            <Link to="/signin" className="text-accent hover:underline">
              Login here
            </Link>
          </p>

          {/* Social Icons */}
          {/* <div className="flex items-center justify-center space-x-4 pt-4">
            {["facebook-f", "instagram", "youtube"].map((icon) => (
              <Link
                key={icon}
                to="#"
                className="text-secondary dark:text-secondary-accent hover:text-primary transition"
              >
                <i className={`fab fa-${icon}`} />
              </Link>
            ))}
          </div> */}
        </form>
      </div>
    </div>
  );
}
